import { cn } from '@tempo-io/sui-class-utils';
import { parseISO } from 'date-fns';
import {
  DateFormat,
  formatDayMonth,
  formatLocalDate,
  fromDateString,
} from '@/common/utils/dateFormats';

const MIN_SOLID_WIDTH_PERCENT = 55;
const MAX_SOLID_WIDTH_PERCENT = 80;

interface TooltipScheduleBarProps {
  forecastedStart: string;
  forecastedFinish: string;
  latestFinish?: string;
  isAtRisk: boolean;
  barColor?: string;
  barTextColor?: string;
}

export const TooltipScheduleBar = ({
  forecastedStart,
  forecastedFinish,
  latestFinish,
  isAtRisk,
  barColor,
  barTextColor,
}: TooltipScheduleBarProps) => {
  const startDate = parseISO(forecastedStart);
  const finishDate = parseISO(forecastedFinish);
  const latestDate = latestFinish ? parseISO(latestFinish) : finishDate;

  const totalDuration = latestDate.getTime() - startDate.getTime();
  const forecastedDuration = finishDate.getTime() - startDate.getTime();
  const forecastedWidthPercent =
    totalDuration > 0
      ? Math.min(100, Math.max(0, (forecastedDuration / totalDuration) * 100))
      : forecastedDuration > 0
        ? 100
        : 0;
  const rawUncertaintyWidthPercent = 100 - forecastedWidthPercent;

  const hasUncertaintySection = !!latestFinish && rawUncertaintyWidthPercent > 0;
  const hasSolidSection = forecastedWidthPercent > 0;

  const solidWidthPercent =
    hasSolidSection && hasUncertaintySection
      ? Math.min(Math.max(forecastedWidthPercent, MIN_SOLID_WIDTH_PERCENT), MAX_SOLID_WIDTH_PERCENT)
      : forecastedWidthPercent;
  const uncertaintyWidthPercent = 100 - solidWidthPercent;

  const formatDate = (dateStr: string) => {
    const localDate = fromDateString(formatLocalDate(dateStr, DateFormat.ISO_DATE));
    return localDate ? formatDayMonth(localDate) : '';
  };

  const dateLabelClassName = isAtRisk
    ? barTextColor
      ? undefined
      : 'text-text-neutral-inverted'
    : 'rounded-full bg-bg-neutral-bold px-1.5 text-text-neutral-inverted';
  const dateLabelStyle = isAtRisk ? { color: barTextColor } : undefined;

  return (
    <div className="relative h-7 @container/tooltip-bar">
      <div className="absolute inset-0 flex items-center">
        {hasSolidSection && (
          <div
            className={cn(
              'absolute h-full flex items-center @container/solid',
              barColor ? undefined : isAtRisk ? 'bg-bg-error' : 'bg-bg-neutral-bold',
              hasUncertaintySection ? 'rounded-l-lg' : 'rounded-lg',
            )}
            style={{
              left: '0%',
              width: `${solidWidthPercent}%`,
              backgroundColor: barColor,
            }}
          >
            <span
              className={cn(
                'absolute left-2 text-xs font-bold hidden @[60px]/solid:inline',
                dateLabelClassName,
              )}
              style={dateLabelStyle}
            >
              {formatDate(forecastedStart)}
            </span>
            <span
              className={cn(
                'absolute right-2 text-xs font-bold hidden @[120px]/solid:inline',
                dateLabelClassName,
              )}
              style={dateLabelStyle}
            >
              {formatDate(forecastedFinish)}
            </span>
          </div>
        )}
        {hasUncertaintySection && (
          <div
            className={cn(
              'absolute h-full border flex items-center overflow-hidden bg-clip-padding bg-bg-surface-base @container/dashed',
              isAtRisk
                ? 'border-border-error-subtle bg-[repeating-linear-gradient(45deg,transparent_0_3px,var(--color-bg-error-subtler)_3px_7px)]'
                : 'border-border-info-subtle bg-[repeating-linear-gradient(45deg,transparent_0_3px,#e9f2fa_3px_7px)]',
              hasSolidSection ? 'rounded-r-lg' : 'rounded-lg',
            )}
            style={{
              left: `${solidWidthPercent}%`,
              width: `${uncertaintyWidthPercent}%`,
            }}
          >
            <span className="absolute right-2 rounded-full bg-bg-surface-base px-1.5 text-xs font-bold text-text-neutral-primary hidden @[40px]/dashed:inline">
              {formatDate(latestFinish)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
